// ─── Frontend view models ─────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
  /** True while SSE chunks are still arriving for this message */
  isStreaming?: boolean;
  chips?: string[];
  items?: string[];
  followUp?: string;
  cardSectionTitle?: string;
  cards?: UniversityCard[];
}

/** Shape of each SSE data chunk from POST /chatBot/stream */
export interface ChatStreamChunk {
  delta?:           string;   // primary content field returned by the stream API
  content?:         string;
  chat_session_id?: string;
  /** Present once, from the stream's `event: badge` block — keys to resolve via
   *  GamificationService.getBadgeDetails() to find out which were newly awarded. */
  badge_keys?:      string[];
  /** Trophy keys (e.g. "AIT1") from the same `event: badge` block. */
  newly_earned_trophies?: string[];
  [key: string]: unknown;
}

/** Envelope returned by POST /chatBot/stream — data holds the raw SSE text */
export interface ChatStreamApiResponse {
  statusCode: number;
  status:     number;
  message:    string;
  data:       string;         // SSE-formatted string: "data: {...}\n\nevent: done\ndata: {...}\n\n"
}

export interface UniversityCard {
  imageUrl: string;
  country: string;
  name: string;
  course: string;
  hasScholarship?: boolean;
}

export interface Conversation {
  id: string;
  title: string;
  updatedAt: Date;
}

export interface JourneyProfile {
  gpa: number;
  budget: number;
  targetCountries: string[];
  shortlistedCount: number;
  matchPercent: number;
}

export type ChatView = 'welcome' | 'chat';

export type SuggestionChipIcon = 'universities' | 'scholarships' | 'compare' | 'applications' | 'abroad';

/** Welcome-screen suggestion chips — wording swaps between the student's own voice and a
 *  tutor/counsellor guiding a student's plans, since the same chat is reached from both
 *  `/ai-counselor` (student) and `/tutor/ai-counselor` (tutor/counsellor). */
export interface SuggestionChipDef {
  icon: SuggestionChipIcon;
  student: string;
  tutor: string;
}

export const SUGGESTION_CHIP_DEFS: SuggestionChipDef[] = [
  { icon: 'universities', student: 'Find best universities for me', tutor: 'Find best universities for my student' },
  { icon: 'scholarships', student: 'Find scholarships', tutor: 'Find scholarships for my student' },
  { icon: 'compare', student: 'Compare my chances', tutor: 'Compare my student\'s chances' },
  { icon: 'applications', student: 'Help with applications', tutor: 'Help my student with applications' },
  { icon: 'abroad', student: 'Study abroad options', tutor: 'Study abroad options for my student' },
];

export function suggestionChipLabels(isTutorOrCounselor: boolean): string[] {
  return SUGGESTION_CHIP_DEFS.map((c) => (isTutorOrCounselor ? c.tutor : c.student));
}

/** i18n key for the "copilot unavailable" banner/placeholder — a tutor/counsellor has no
 *  tutor/counsellor of their own to connect with, so they're pointed to admin instead. */
export function copilotUnavailableMessageKey(isTutorOrCounselor: boolean): string {
  return isTutorOrCounselor ? 'CopilotUnavailableMessageAdmin' : 'CopilotUnavailableMessage';
}

// ─── API DTOs ─────────────────────────────────────────────────────────────────

/** Generic API envelope — status is 1 (success) or 0 (failure) */
export interface ChatBotApiResponse<T> {
  statusCode?: number;
  status: number;
  message: string;
  data: T;
}

/** POST /chatBot/chat — request body.
 *  Send chat_session_id as "" for a new session; the response returns the assigned id. */
export interface ChatSendPayload {
  chat_session_id: string;
  message: string;
}

/** POST /chatBot/speech-to-text — response data */
export interface SpeechToTextResponseData {
  text: string;
  language?: string | null;
  duration?: number | null;
}

/** POST /chatBot/chat — response data */
export interface ChatSendResponseData {
  chat_session_id?: string;
  reply?: string;
  response?: string;
  message?: string;
  content?: string;
  [key: string]: unknown;
}

/** Shape of one session from GET /chatBot/chat-history/sessions */
export interface ChatHistorySessionDto {
  chat_session_id: string;
  user_id?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
}

/** data wrapper for GET /chatBot/chat-history/sessions */
export interface ChatSessionsResponseData {
  sessions: ChatHistorySessionDto[];
  has_more?: boolean;
  next_cursor?: string | null;
}

/** Shape of one message from GET /chatBot/chat-history/messages */
export interface ChatHistoryMessageDto {
  message_id: string;
  chat_session_id?: string;
  user_id?: string;
  role: 'user' | 'assistant';
  message: string;
  created_at?: string;
}

/** data wrapper for GET /chatBot/chat-history/messages */
export interface ChatMessagesResponseData {
  messages: ChatHistoryMessageDto[];
  has_more?: boolean;
  next_cursor?: string | null;
}

/** `chatbot` field from GET /user/chatbot — a per-user disable window */
export interface ChatbotConfig {
  disableStatus: boolean;
  from_date?: string;
  to_date?: string;
}

/** data wrapper for GET /user/chatbot */
export interface ChatbotStatusResponseData {
  _id?: string;
  email?: string;
  chatbot: ChatbotConfig;
}

/** True while `chatbot.disableStatus` is set and now falls within [from_date, to_date].
 *  Shared by the AI Counsellor page and the Copilot popup so both block in sync. */
export function isChatbotDisabled(chatbot: ChatbotConfig | undefined): boolean {
  if (!chatbot?.disableStatus) return false;
  const now = Date.now();
  const from = chatbot.from_date ? new Date(chatbot.from_date).getTime() : -Infinity;
  const to = chatbot.to_date ? new Date(chatbot.to_date).getTime() : Infinity;
  return now >= from && now <= to;
}

/** Shared mapper — used by both the AI Counsellor page and the Copilot popup so history renders identically in either surface. */
export function mapChatHistoryMessage(m: ChatHistoryMessageDto): ChatMessage {
  // API returns "assistant" for bot messages
  const role: 'user' | 'bot' = m.role === 'user' ? 'user' : 'bot';
  return {
    id: m.message_id,
    role,
    content: m.message,
    timestamp: m.created_at ? new Date(m.created_at) : new Date(),
  };
}
